/**
 * SELWA-Designsystem: PrimeVue-Aura-Preset mit Markentokens.
 *
 * Marke (aus dem Logo abgeleitet):
 *   - Amber   #FFB600  Primärfarbe / Fokusringe / aktive Navigation
 *   - Tinte   #141210  Kopfleiste, Text
 *   - Papier  #FAF7F2  App-Hintergrund (warmes Papier)
 *   - Erfolg  #2E7D32
 *   - Gefahr  #C62828
 *
 * Nur Light-Theme (`darkModeSelector: 'none'` in `main.ts`).
 */
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

/** Amber-Skala 50–950 um #FFB600 (500) herum. */
export const amber = {
  50: '#fff8e6',
  100: '#ffefc0',
  200: '#ffe195',
  300: '#ffd264',
  400: '#ffc42f',
  500: '#ffb600',
  600: '#e09e00',
  700: '#b87f00',
  800: '#8f6200',
  900: '#6b4900',
  950: '#3d2a00',
} as const

/** Warme Graustufen: 0 = weiß … 950 = Tintenschwarz (#141210). */
export const paperSurface = {
  0: '#ffffff',
  50: '#faf7f2',
  100: '#f2ede4',
  200: '#e6dfd2',
  300: '#d5ccbb',
  400: '#b3a896',
  500: '#8c8272',
  600: '#6b6357',
  700: '#4e483f',
  800: '#332f29',
  900: '#211e1a',
  950: '#141210',
} as const

export const brand = {
  ink: '#141210',
  paper: '#faf7f2',
  amber: '#ffb600',
  success: '#2e7d32',
  danger: '#c62828',
} as const

export const SelwaPreset = definePreset(Aura, {
  primitive: {
    amber,
    green: {
      50: '#eef7ee',
      100: '#d3e9d4',
      200: '#b0d8b2',
      300: '#89c48c',
      400: '#5da762',
      500: '#2e7d32',
      600: '#28702c',
      700: '#215e25',
      800: '#1a4b1e',
      900: '#133716',
      950: '#0b210e',
    },
    red: {
      50: '#fdeded',
      100: '#f9d2d2',
      200: '#f0aaaa',
      300: '#e57f7f',
      400: '#d75454',
      500: '#c62828',
      600: '#b12222',
      700: '#941c1c',
      800: '#761616',
      900: '#561010',
      950: '#340909',
    },
  },
  semantic: {
    primary: amber,
    // Fokus: immer amberfarbener Ring, gut sichtbar auf Papier und Tinte.
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.500}',
      offset: '2px',
      shadow: '0 0 0 4px rgba(255, 182, 0, 0.28)',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      borderRadius: '8px',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.500}',
        offset: '0',
        shadow: '0 0 0 4px rgba(255, 182, 0, 0.28)',
      },
    },
    content: {
      borderRadius: '10px',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{amber.500}',
          // Amber trägt keinen weißen Text – Tinte für AA-Kontrast.
          contrastColor: brand.ink,
          hoverColor: '{amber.600}',
          activeColor: '{amber.700}',
        },
        highlight: {
          background: '{amber.100}',
          focusBackground: '{amber.200}',
          color: brand.ink,
          focusColor: brand.ink,
        },
        surface: paperSurface,
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{surface.950}',
        },
        text: {
          color: '{surface.950}',
          hoverColor: '{surface.950}',
          mutedColor: '{surface.600}',
          hoverMutedColor: '{surface.700}',
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.100}',
          filledBackground: '{surface.50}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{amber.500}',
          invalidBorderColor: brand.danger,
          color: '{surface.950}',
          placeholderColor: '{surface.500}',
          floatLabelColor: '{surface.600}',
        },
      },
      // Light-only: Dark-Scheme spiegelt Light, damit nichts unlesbar wird,
      // falls der Selektor je aktiviert wird.
      dark: {
        primary: {
          color: '{amber.500}',
          contrastColor: brand.ink,
          hoverColor: '{amber.400}',
          activeColor: '{amber.300}',
        },
        surface: paperSurface,
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: '8px',
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{amber.500}',
              hoverBackground: '{amber.600}',
              activeBackground: '{amber.700}',
              borderColor: '{amber.500}',
              hoverBorderColor: '{amber.600}',
              activeBorderColor: '{amber.700}',
              color: brand.ink,
              hoverColor: brand.ink,
              activeColor: brand.ink,
            },
          },
        },
      },
    },
  },
})

export default SelwaPreset
